import React from 'react';
import { FormContainer } from '../../../form/FormContainer';
import FormItem from '../../../form/FormItem';
import { SCHEDULE_FORM_WIDGET_FIELD_NAMES } from '../constants';
import useScheduleFormWidgetContext from '../useScheduleFormWidgetContext';
import { resolveStateFieldName } from '../utils/form-utils';
import ScheduleCalendarFormWidget from './ScheduleCalendarFormWidget';
import ScheduleDateTimeFormWidget from './ScheduleDateTimeFormWidget';
import ScheduleDurationUnitFormWidget from './ScheduleDurationUnitFormWidget';
import ScheduleDurationValueFormWidget from './ScheduleDurationValueFormWidget';
import ScheduleLocationFormWidget from './ScheduleLocationFormWidget';
import ScheduleNameFormWidget from './ScheduleNameFormWidget';

export interface ScheduleFormCompactWidgetProps {
    name?: string;

    disabled?: boolean;

    /**
     * Indicates if this widget is being used as a follower in a sequence of schedules.
     * When true only the field that's the input for schedule calculation (endDate/Time OR duration) is enabled.
     * All other fields remain disabled since they are derived from the preceding schedule.
     *
     * @default false
     */
    isSequenceFollower?: boolean;
}

/**
 * Presentational component for compact horizontal schedule form layout.
 *
 * Layout structure (2 lines):
 * Line 1: Name | Unit | Start Date | Start Time | (Duration OR End Date/Time)
 * Line 2: Location | Calendar
 *
 * Note: "Use Duration" toggle is hidden in this variant.
 */
export default function ScheduleFormCompactWidget({
    name,
    disabled,
    isSequenceFollower = false
}: ScheduleFormCompactWidgetProps) {
    const { getFormStateByFieldName } = useScheduleFormWidgetContext();

    const durationSpecifiedFieldName = resolveStateFieldName([
        name,
        SCHEDULE_FORM_WIDGET_FIELD_NAMES.DURATION_SPECIFIED
    ]);
    const isDurationSpecified = getFormStateByFieldName(durationSpecifiedFieldName) as boolean;

    const isWidgetDisabled = disabled || isSequenceFollower;

    return (
        <>
            <FormContainer>
                <FormItem quarter>
                    <ScheduleNameFormWidget name={name} disabled={isWidgetDisabled} />
                </FormItem>
                <FormItem span={2}>
                    <ScheduleDurationUnitFormWidget name={name} disabled={isWidgetDisabled} />
                </FormItem>
                <FormItem span={7}>
                    <ScheduleDateTimeFormWidget variant="start" name={name} disabled={isWidgetDisabled} />
                    {isDurationSpecified ? (
                        <ScheduleDurationValueFormWidget name={name} disabled={disabled} />
                    ) : (
                        <ScheduleDateTimeFormWidget variant="end" name={name} disabled={disabled} />
                    )}
                </FormItem>
            </FormContainer>

            {/* Line 2: Location and Calendar */}
            <FormContainer>
                <FormItem half>
                    <ScheduleLocationFormWidget name={name} disabled={isWidgetDisabled} />
                </FormItem>
                <FormItem half>
                    <ScheduleCalendarFormWidget name={name} disabled={isWidgetDisabled} />
                </FormItem>
            </FormContainer>
        </>
    );
}
